import { CustomAppEvents } from 'core/common/entities';
import { CookiesService } from 'core/common/services';
import { checkIsClientSide } from 'core/common/utils/checkIsClientSide';
import { CookieConsentService } from 'core/cookie-consent/interfaces';
import { AnalyticsPixelId, TikTokEvent, TiktokParams, TikTokMethods } from '../../entities';
import { TikTokService } from '../../interfaces';

const noop: TikTokMethods & { isAvailable: false } = {
  isAvailable: false,
  revokeConsent: () => {},
  grantConsent: () => {},
  load: () => {},
  page: () => {},
  identify: () => {},
  track: () => {},
};

export class HttpTikTokService implements TikTokService {
  private readonly passedPixelIds: Array<AnalyticsPixelId> = [];
  private initializedPixelIds: Array<AnalyticsPixelId> = [];

  constructor(
    private readonly cookiesService: CookiesService,
    private readonly cookieConsentService: CookieConsentService,
    pixelIds?: Array<AnalyticsPixelId>,
  ) {
    this.passedPixelIds = !!pixelIds?.length ? pixelIds : [];
    this.cookieConsentService.addConsentLoadListener(this.onConsentLoad(this.passedPixelIds));
    this.cookieConsentService.addConsentChangeListener(this.onConsentChange(this.passedPixelIds));
  }

  isTtqRequired(): boolean {
    return !!this.passedPixelIds.length;
  }

  isTtqAvailable(): boolean {
    return this.ttq.isAvailable;
  }

  track(eventName: TikTokEvent, params?: TiktokParams) {
    const ttp = this.cookiesService.get('_ttp'); // Cookie id
    const ttclid = this.cookiesService.get('ttclid'); // TikTok Click ID
    const email = params?.email;

    if (email && typeof email === 'string') {
      this.ttq.identify({ email: email.trim().toLowerCase() });
    }
    this.ttq.track(eventName, { ttp, ttclid, ...params });
  }

  private get ttq(): TikTokMethods & { isAvailable: boolean } {
    if (!checkIsClientSide() || !window.ttq) {
      return noop;
    }

    const ttq = window.ttq;

    return {
      isAvailable: true,
      revokeConsent: () => ttq.revokeConsent(),
      grantConsent: () => ttq.grantConsent(),
      load: (pixel: AnalyticsPixelId) => ttq.load(pixel),
      page: () => ttq.page(),
      identify: (params: { email: string }) => ttq.identify(params),
      track: (eventName: TikTokEvent, params: TiktokParams) => ttq.track(eventName, params),
    };
  }

  private onConsentLoad =
    (pixels: Array<AnalyticsPixelId>) => (consentState: { TARGETING: boolean }) => {
      if (consentState.TARGETING) {
        this.initializePixels(pixels);
      }
    };

  private onConsentChange =
    (pixels: Array<AnalyticsPixelId>) => (consentState: { TARGETING: boolean }) => {
      if (consentState.TARGETING) {
        this.initializePixels(pixels);
        return;
      }
      this.ttq.revokeConsent();
    };

  private initializePixels(pixels: Array<AnalyticsPixelId>) {
    if (this.ttq.isAvailable) {
      this.doInitializePixels(pixels);
      return;
    }

    document.addEventListener(
      CustomAppEvents.TIKTOK_PIXEL_LOADED,
      () => {
        this.doInitializePixels(pixels);
      },
      { once: true },
    );
  }

  private doInitializePixels(pixelsToInitialize: Array<AnalyticsPixelId>) {
    this.ttq.grantConsent();
    pixelsToInitialize.forEach((pixel) => {
      if (this.initializedPixelIds.includes(pixel)) {
        return;
      }

      this.ttq.load(pixel);
      this.ttq.page();
      this.initializedPixelIds.push(pixel);
    });
  }
}
